import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Shield, UserCog, Eye, KeyRound } from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_META = {
  admin:    { label: "Admin",    icon: Shield,  className: "bg-emerald-500/15 text-emerald-400 border-emerald-600/40" },
  reviewer: { label: "Reviewer", icon: UserCog, className: "bg-amber-500/15 text-amber-400 border-amber-600/40" },
  viewer:   { label: "Viewer",   icon: Eye,     className: "bg-zinc-500/15 text-zinc-300 border-zinc-600/40" },
};

function policyHint(policy, role) {
  const p = policy?.[role];
  if (!p) return "";
  const bits = [`${p.min_len}+ chars`];
  if (p.mixed_case) bits.push("mixed case");
  if (p.digit_or_symbol) bits.push("digit or symbol");
  return bits.join(" · ");
}

function RoleBadge({ role }) {
  const meta = ROLE_META[role] || ROLE_META.viewer;
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${meta.className}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </Badge>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "viewer" });
  const [newPwd, setNewPwd] = useState("");
  const [policy, setPolicy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, pRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/auth/password-policy`),
      ]);
      setUsers(uRes.data);
      setPolicy(pRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createUser = async () => {
    try {
      await axios.post(`${API}/users`, form);
      toast.success(`User ${form.email} created`);
      setOpenCreate(false);
      setForm({ email: "", name: "", password: "", role: "viewer" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const updateRole = async (u, role) => {
    try {
      await axios.patch(`${API}/users/${u.id}`, { role });
      toast.success(`Role updated to ${role}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"?`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submitReset = async () => {
    try {
      await axios.patch(`${API}/users/${resetUser.id}`, { password: newPwd });
      toast.success("Password reset");
      setResetUser(null);
      setNewPwd("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-[Chivo] font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage who can access the MIS platform and what they can do.</p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="bg-zinc-950 text-white hover:bg-zinc-800">
              <Plus className="h-4 w-4 mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-user-dialog">
            <DialogHeader>
              <DialogTitle>Add user</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
              </div>
              <div className="space-y-2">
                <Label>Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
              </div>
              <div className="space-y-2">
                <Label>Temporary password</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
                <p className="text-xs text-zinc-500">
                  {policy ? `Policy for ${form.role}: ${policyHint(policy, form.role)}.` : null}
                  {" "}User must change this on first login.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin — full access incl. user mgmt, month lock</SelectItem>
                    <SelectItem value="reviewer">Reviewer — upload data, post Audit JVs</SelectItem>
                    <SelectItem value="viewer">Viewer — read-only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenCreate(false)}>Cancel</Button>
              <Button onClick={createUser} data-testid="create-user-submit">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-zinc-200">
        <CardHeader>
          <CardTitle className="text-base">All users ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-6">Loading…</TableCell></TableRow>
              )}
              {!loading && users.map((u) => {
                const isMe = me && u.id === me.id;
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-medium">
                      {u.email} {isMe && <span className="text-xs text-zinc-500 ml-1">(you)</span>}
                    </TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>
                      {isMe ? (
                        <RoleBadge role={u.role} />
                      ) : (
                        <Select value={u.role} onValueChange={(v) => updateRole(u, v)}>
                          <SelectTrigger className="w-36 h-8" data-testid={`role-select-${u.email}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="reviewer">Reviewer</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className="text-zinc-500 text-sm">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setResetUser(u); setNewPwd(""); }}
                        data-testid={`reset-pwd-${u.email}`}
                      >
                        <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        disabled={isMe}
                        onClick={() => deleteUser(u)}
                        data-testid={`delete-user-${u.email}`}
                        className="text-rose-600 hover:text-rose-700"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && users.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-6">No users yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password — {resetUser?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>New password</Label>
            <Input type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="reset-password-input" />
            <p className="text-xs text-zinc-500">User will be asked to change this on next login.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)}>Cancel</Button>
            <Button onClick={submitReset} disabled={newPwd.length < 6} data-testid="reset-password-submit">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
